import React, { AnchorHTMLAttributes } from "react";
import { Link, LinkProps } from "react-router-dom";
import { ButtonProps as ButtonPropsRoot } from "@common/types";
import { ButtonPrimitive } from "@app/components/ui/button";
import { useDocumentDirection } from "@app/hooks";
import { cn } from "@app/lib/utils";
import { Tooltip } from "../helpers";
import { Loading } from "../loading";

export type ButtonProps = Omit<ButtonPropsRoot, "onClick"> & {
  as?: string;
  name?: string;
} & (AnchorHTMLAttributes<HTMLAnchorElement> | LinkProps);

export type IconButtonProps = ButtonProps & {
  "aria-label": string;
  center?: never;
  children?: never;
  icon: React.ReactNode;
  iconOnly?: never;
  tooltip?: React.ReactNode;
};

const variantMap = {
  primary: "default",
  secondary: "outline",
  success: "success",
  danger: "destructive",
  warning: "warning",
  info: "info",
  light: "outline",
  text: "ghost",
  dark: "outline",
  gray: "outline",
  "outline-primary": "outlinePrimary",
  "outline-success": "outlineSuccess",
  "outline-danger": "outlineDestructive",
  "outline-warning": "outlineWarning",
  "outline-info": "outlineInfo",
  "outline-light": "outlineMuted",
  "outline-dark": "outlineMuted",
  link: "link",
} as const;

function normalizeButtonClassName(className: ButtonProps["className"]) {
  if (typeof className !== "string") {
    return className;
  }

  return className
    .replace(
      /(^|\s)(?:w-full|min-w-full|sm:w-auto|md:w-auto|lg:w-auto|xl:w-auto|2xl:w-auto)(?=\s|$)/g,
      " ",
    )
    .replace(/\s+/g, " ")
    .trim();
}

function getRequestedWidth(className: ButtonProps["className"]) {
  if (typeof className !== "string") {
    return { full: false, mobile: false };
  }

  const requestsFullWidth = /(^|\s)(?:w-full|min-w-full)(?=\s|$)/.test(
    className,
  );
  const resetsAtBreakpoint =
    /(^|\s)(?:sm:w-auto|md:w-auto|lg:w-auto|xl:w-auto|2xl:w-auto)(?=\s|$)/.test(
      className,
    );

  return {
    full: requestsFullWidth && !resetsAtBreakpoint,
    mobile: requestsFullWidth && resetsAtBreakpoint,
  };
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant,
      as,
      icon,
      iconPosition = "start",
      iconClassName = "",
      children,
      className,
      loading,
      name,
      rounded = true,
      center,
      fullWidth,
      iconOnly,
      padding = "medium",
      block,
      ...rest
    },
    ref,
  ) {
    const controlDir = useDocumentDirection();
    const isBare = padding === "none";
    const finalIcon = icon;
    const isIconOnly = Boolean(iconOnly ?? (finalIcon && !children && !isBare));
    const requestedWidth = getRequestedWidth(className);
    const isMobileFullWidth = fullWidth === "mobile" || requestedWidth.mobile;
    const isFullWidth =
      fullWidth === true || Boolean(block) || requestedWidth.full;
    const normalizedClassName = normalizeButtonClassName(className);
    const Component: any =
      as === "link"
        ? Link
        : as ||
          ("to" in rest && rest.to
            ? Link
            : "href" in rest && rest.href
              ? "a"
              : "button");
    const primitiveVariant = isBare
      ? "ghost"
      : variant
        ? variantMap[variant]
        : "outline";
    const primitiveSize = isIconOnly
      ? "icon"
      : padding === "large"
        ? "lg"
        : padding === "small"
          ? "sm"
          : padding === "none"
            ? "none"
            : "default";
    const buttonClassName = cn(
      isFullWidth
        ? "w-full"
        : isMobileFullWidth
          ? "w-full sm:w-auto"
          : !isIconOnly && "w-fit",
      isFullWidth
        ? "flex"
        : isMobileFullWidth
          ? "flex sm:inline-flex"
          : "inline-flex",
      loading && "relative",
      center && "justify-center",
      !rounded && "rounded-none",
      isBare &&
        "min-h-0 border-0 bg-transparent shadow-none hover:bg-transparent active:bg-transparent",
      normalizedClassName,
    );
    const content = (
      <>
        {loading && (
          <div className="absolute -inset-px">
            <Loading />
          </div>
        )}
        {finalIcon ? (
          children ? (
            <span
              className="inline-flex min-w-0 items-center justify-center gap-2 leading-5"
              dir={(rest as any).dir || controlDir}
            >
              {iconPosition === "start" && (
                <span
                  className={cn(
                    "inline-flex h-5 w-5 flex-shrink-0 items-center justify-center [&_svg]:h-4 [&_svg]:w-4",
                    iconClassName,
                  )}
                >
                  {finalIcon}
                </span>
              )}
              <span className="min-w-0 truncate leading-5">{children}</span>
              {iconPosition === "end" && (
                <span
                  className={cn(
                    "inline-flex h-5 w-5 flex-shrink-0 items-center justify-center [&_svg]:h-4 [&_svg]:w-4",
                    iconClassName,
                  )}
                >
                  {finalIcon}
                </span>
              )}
            </span>
          ) : (
            <span
              className={cn(
                "inline-flex h-5 w-5 items-center justify-center [&_svg]:h-4 [&_svg]:w-4",
                iconClassName,
              )}
            >
              {finalIcon}
            </span>
          )
        ) : (
          children
        )}
      </>
    );

    if (Component === "button") {
      return (
        <ButtonPrimitive
          ref={ref}
          className={buttonClassName}
          variant={primitiveVariant}
          size={primitiveSize}
          {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
          disabled={Boolean((rest as any).disabled || loading)}
          data-action-control="true"
          data-full-width={isFullWidth ? "true" : undefined}
          data-icon-only={isIconOnly ? "true" : undefined}
          name={name}
          dir={(rest as any).dir || controlDir}
        >
          {content}
        </ButtonPrimitive>
      );
    }

    return (
      <ButtonPrimitive
        asChild
        ref={ref}
        className={buttonClassName}
        disabled={Boolean((rest as any).disabled || loading)}
        data-action-control="true"
        data-full-width={isFullWidth ? "true" : undefined}
        data-icon-only={isIconOnly ? "true" : undefined}
        variant={primitiveVariant}
        size={primitiveSize}
      >
        <Component name={name} {...rest} dir={(rest as any).dir || controlDir}>
          {content}
        </Component>
      </ButtonPrimitive>
    );
  },
);

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton({ tooltip, title, ...props }, ref) {
    const label = props["aria-label"];
    const button = (
      <Button
        ref={ref}
        iconOnly
        title={title || (typeof label === "string" ? label : undefined)}
        {...props}
      />
    );

    if (!tooltip) {
      return button;
    }

    return (
      <Tooltip tooltip={tooltip} placement="top">
        {button}
      </Tooltip>
    );
  },
);
